import PropTypes from 'prop-types';
import {
  useReducer,
  useCallback,
  useMemo,
  useContext,
} from 'react';

import {
  useDisplay,
  useTownAssets,
} from '../stores';
import TownConfigContext from '../stores/townConfig/TownConfigContext';
import {
  displayJson,
  logError,
} from '../utils';

import {
  Button,
} from './buttons';
import {
  ConfirmPopup,
  FullPopup,
  Row,
} from './display';
import PaxityProgress from './PaxityProgress';
import WizardContext from './WizardContext';
import WizardStepsBar from './WizardStepsBar';

const initialState = {
  // Current wizard page
  // currentPage: 'wizardStart',
  // All pages variables, maintained by pages contents:
  // page: {},
  // Get set when user must confirm to close wizard:
  mustConfirmClose: false,
  // Next page, if any
  nextPage: undefined,
  // Previous page
  prevPage: undefined,
  // Generic busy flag, set by content when needed
  pageBusy: false,
  // For confirming cancel
  confirmClosePending: false,
  // For confirming final action with text gate
  confirmActionPending: false,
  // Validation state, enables going to next page / confirmation
  validation: {
    valid: {},
    errors: {},
    pageValid: false,
  },
  // Set when we loaded a page, but have no info yet on that page's
  // behaviour
  waitingOnPageInfo: true,
  // Function called to verify the whole current page's content is
  // valid, we can navigate away
  pageStateValidator: undefined,
  // Optional async function run when next is clicked, before leaving the page: answers false to
  // stay (a save that failed, say), the page having told the user why
  beforeNextPage: undefined,
  // Function called at the wizard's end
  confirmPage: undefined,
  // Label of the button calling confirmPage, when the page's action is not a plain confirmation
  confirmLabel: undefined,
  // Optional confirmation options for text-based gate
  // { confirmText: 'WORD', confirmMessage: 'Translation key' }
  confirmOptions: undefined,
  // Optional node drawn beside the next button, for what still holds the page back
  nextHint: undefined,
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return {
        ...initialState,
        currentPage: action.initialPage,
        page: { ...action.defaultPageState },
      };

    case 'setPageInfo': {
      // New loaded page needs to call this method before anything else
      const newState = {
        ...state,
        prevPage: action.prevPage,
        nextPage: action.nextPage,
        pageStateValidator: action.pageStateValidator,
        beforeNextPage: action.beforeNextPage,
        confirmPage: action.confirmPage,
        confirmLabel: action.confirmLabel,
        confirmOptions: action.confirmOptions,
        nextHint: action.nextHint,
        waitingOnPageInfo: false,
        pageBusy: false,
      };

      // When entering the page, don't touch the state, but make sure the validator is run
      // (things from other pages might affect this page's result)
      const validated = action.pageStateValidator(newState.page);

      newState.validation = {
        valid: {
          ...state.validation.valid,
          ...validated.valid,
        },
        errors: {
          ...state.validation.errors,
          ...validated.errors,
        },
        pageValid: validated.pageValid,
      };

      return newState;
    }

    case 'setPrevPage':
      return {
        ...state,
        prevPage: action.prevPage,
      };

    case 'setNextPage':
      return {
        ...state,
        nextPage: action.nextPage,
      };

    case 'setMustConfirmClose':
      return {
        ...state,
        mustConfirmClose: action.mustConfirmClose,
      };

    case 'nextPage': {
      const newState = {
        ...state,
        currentPage: state.nextPage,
        waitingOnPageInfo: true,
        validation: {
          valid: state.validation.valid,
          errors: state.validation.errors,
          pageValid: false,
        },
        pageStateValidator: undefined,
        beforeNextPage: undefined,
        confirmPage: undefined,
        pageBusy: false,
      };

      return newState;
    }

    case 'goToPage': {
      return {
        ...state,
        currentPage: action.page,
        waitingOnPageInfo: true,
        validation: {
          valid: state.validation.valid,
          errors: state.validation.errors,
          pageValid: false,
        },
        pageStateValidator: undefined,
        beforeNextPage: undefined,
        confirmPage: undefined,
        pageBusy: false,
      };
    }

    case 'prevPage': {
      const newState = {
        ...state,
        currentPage: state.prevPage,
        waitingOnPageInfo: true,
        validation: {
          valid: state.validation.valid,
          errors: state.validation.errors,
          pageValid: false,
        },
        pageStateValidator: undefined,
        beforeNextPage: undefined,
        confirmPage: undefined,
        pageBusy: false,
      };

      return newState;
    }

    case 'setValue': {
      if (state[action.name] === action.value) return state;

      const newState = { ...state };
      newState[action.name] = action.value;

      return newState;
    }

    case 'setPageState': {
      if (!(action.pageState || action.validate)) return state;

      if (state.waitingOnPageInfo) {
        logError(
          'Call setPageInfo BEFORE calling any other method after loading page',
          'WizardPlayer.setPageState.missingPageInfo',
          { currentPage: state.currentPage },
        );

        return state;
      }

      const newState = {
        ...state,
      };

      if (action.pageState) {
        newState.page = {
          ...state.page,
          ...action.pageState,
        };
      }

      if (action.validate) {
        const validated = state.pageStateValidator(newState.page);

        newState.validation = {
          valid: {
            ...state.validation.valid,
            ...validated.valid,
          },
          errors: {
            ...state.validation.errors,
            ...validated.errors,
          },
          pageValid: validated.pageValid,
        };
      }

      return newState;
    }

    case 'setPageBusy':
      return {
        ...state,
        pageBusy: action.pageBusy,
      };

    default:
      logError(
        `WizardPlayer - ignore action=${displayJson(action)}`,
        'WizardPlayer.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

function WizardPlayer(props) {
  const {
    pages,
    closeCreate,
    initialPage = 'wizardStart',
    defaultPageState = {},
    showProgressBar = false,
    noTownConfig = false,
    mobileFullScreen = false,
  } = props;

  const { assets } = useTownAssets();
  const { display } = useDisplay(false);
  const narrow = Boolean(display?.narrow);
  const {
    translate,
  } = assets;

  //
  // Pages read the customer's configuration, so the wizard waits for it. The
  // public invite route has no such configuration and no provider above it, so
  // the context is read directly rather than through the throwing hook.
  //
  const townConfigContext = useContext(TownConfigContext);
  const configReady = noTownConfig || Boolean(townConfigContext?.townConfig?.ready);

  // Initialize state with the specified initial page
  const [state, dispatch] = useReducer(
    stateReducer,
    {
      ...initialState,
      currentPage: initialPage,
      page: { ...defaultPageState },
    },
  );

  const {
    confirmPage,
    beforeNextPage,
  } = state;

  const resetAndClose = useCallback(
    () => {
      // Reset state, in case we re-open this window later on
      dispatch({
        type: 'reset',
        initialPage,
        defaultPageState,
      });
      closeCreate();
    },
    [closeCreate, defaultPageState, initialPage],
  );

  const cancel = useCallback(
    () => {
      if (state.mustConfirmClose && (!state.confirmClosePending)) {
        dispatch({
          type: 'setValue',
          name: 'confirmClosePending',
          value: true,
        });
      } else {
        resetAndClose();
      }
    },
    [resetAndClose, state.confirmClosePending, state.mustConfirmClose],
  );

  const rejectCancel = useCallback(
    () => {
      dispatch({
        type: 'setValue',
        name: 'confirmClosePending',
        value: false,
      });
    },
    [],
  );

  const nextClick = useCallback(
    async () => {
      if (beforeNextPage) {
        dispatch({
          type: 'setPageBusy',
          pageBusy: true,
        });
        const proceed = await beforeNextPage();
        dispatch({
          type: 'setPageBusy',
          pageBusy: false,
        });
        if (!proceed) return;
      }
      dispatch({ type: 'nextPage' });
    },
    [beforeNextPage],
  );

  const backClick = useCallback(
    () => { dispatch({ type: 'prevPage' }); },
    [],
  );

  // For a page the ordinary next/back path cannot reach: the outcome page a
  // confirmation lands on, when the wizard reports rather than closes
  const goToPage = useCallback(
    (page) => {
      dispatch({
        type: 'goToPage',
        page,
      });
    },
    [],
  );

  const setPageState = useCallback(
    (pageState, validate = true) => {
      dispatch({
        type: 'setPageState',
        pageState,
        validate,
      });
    },
    [],
  );

  const setMustConfirmClose = useCallback(
    (mustConfirmClose) => {
      dispatch({
        type: 'setMustConfirmClose',
        mustConfirmClose,
      });
    },
    [],
  );

  const setPageBusy = useCallback(
    (pageBusy) => {
      dispatch({
        type: 'setPageBusy',
        pageBusy,
      });
    },
    [],
  );

  const setPageInfo = useCallback(
    (info) => {
      if (!info.pageStateValidator) {
        logError(
          'WizardPlayer setPageInfo pageStateValidator undefined',
          'WizardPlayer.setPageInfo.missingValidator',
          { infoKeys: info && Object.keys(info) },
        );
      } else {
        dispatch({
          type: 'setPageInfo',
          prevPage: info.prevPage,
          nextPage: info.nextPage,
          pageStateValidator: info.pageStateValidator,
          beforeNextPage: info.beforeNextPage,
          confirmPage: info.confirmPage,
          confirmLabel: info.confirmLabel,
          confirmOptions: info.confirmOptions,
          nextHint: info.nextHint,
        });
      }
    },
    [],
  );

  const endWizard = useCallback(
    async () => {
      // If confirmOptions is set, show text-based confirmation first
      if (state.confirmOptions && (!state.confirmActionPending)) {
        dispatch({
          type: 'setValue',
          name: 'confirmActionPending',
          value: true,
        });
        return;
      }

      // Proceed with actual confirmation
      let success;

      if (!state.confirmPage) {
        logError(
          'WizardPlayer endWizard state.confirmPage undefined',
          'WizardPlayer.endWizard.missingConfirmPage',
          { currentPage: state.currentPage },
        );
      } else {
        success = await state.confirmPage();
      }

      if (success) {
        resetAndClose();
      }
    },
    [resetAndClose, state],
  );

  const confirmAction = useCallback(
    async () => {
      // Close the confirmation dialog
      dispatch({
        type: 'setValue',
        name: 'confirmActionPending',
        value: false,
      });

      // Execute the actual confirmation
      let success;
      if (confirmPage) {
        success = await confirmPage();
      }

      if (success) {
        resetAndClose();
      }
    },
    [resetAndClose, confirmPage],
  );

  const rejectAction = useCallback(
    () => {
      dispatch({
        type: 'setValue',
        name: 'confirmActionPending',
        value: false,
      });
    },
    [],
  );

  // Create the context value with wizard controls
  const wizardContextValue = useMemo(
    () => ({
      // Current page state; page content can place what it wants
      // in this object:
      state: state.page,

      // Validation state
      // validation: {
      //   valid: {}          : list of valid flags (set by page content
      //                         through pageStateValidator)
      //   errors: {}         : list of invalid flags (set by page content
      //                         through pageStateValidator)
      //   pageValid:         : set when page got entirely validated
      //                         by pageStateValidator
      // },
      validation: state.validation,

      // Called by page content when page is loaded; upload different items
      // setPageInfo(info), where
      // info = {
      //   prevPage:            Previous page ID
      //   nextPage:            Next page ID
      //                         (cleared for last page)
      //   pageStateValidator:  Page content validator
      //                         (conditions next/confirm buttons)
      //   beforeNextPage:      Optional async function run on next, the
      //                         page moving on only when it answers true
      //   confirmPage:         Wizard end function called by clicking
      //                         the confirm button
      //   confirmLabel:        Optional label of that button, translated,
      //                         instead of the confirm word
      //   confirmOptions:      Optional text-based confirmation gate
      //                         { confirmText: 'WORD', confirmMessage: 'translationKey' }
      //                         When set, user must type confirmText before
      //                         confirmPage executes
      //   nextHint:            Optional node drawn beside the next button,
      //                         saying what still holds the page back
      // }
      setPageInfo,

      // Set page state content:
      setPageState,

      // Call to signal that from now on, user must confirm before
      // closing this wizard dialog window:
      setMustConfirmClose,

      // Call to set/clear a busy flag, disabling navigation buttons
      setPageBusy,

      // Jump to a named page:
      goToPage,
    }),
    [
      setMustConfirmClose, setPageBusy, goToPage,
      state.page, state.validation, setPageState, setPageInfo,
    ],
  );

  const content = useMemo(
    () => {
      if (!configReady) {
        return <PaxityProgress />;
      }

      const currentPageInfo = pages[state.currentPage];
      if (!currentPageInfo) {
        console.error(`Page not found: ${state.currentPage}`);
        return <div>Error: Page not found</div>;
      }

      const PageContent = currentPageInfo.Content;
      const showBackButton = Boolean(state.prevPage);

      return (
        <>
          {/* Render the current page component */}
          <PageContent />

          <div className="flex-1" />

          {/* Navigation controls */}
          <Row key="controls" fullw between className="shrink-0">
            {showBackButton
              ? (
                  <Button
                    onClick={backClick}
                    label={translate('back')}
                    imgSrc="/images/svg/gui/back.svg"
                    pending={state.pageBusy || state.waitingOnPageInfo}
                    neutralColor
                  />
                )
              : <div className="h-1 w-1" />}

            {state.nextPage && (
              <Row crossCenter className="gap-4">
                {state.nextHint ?? null}
                <Button
                  onClick={nextClick}
                  label={translate('next')}
                  imgSrc="/images/svg/gui/next.svg"
                  pending={state.pageBusy || state.waitingOnPageInfo}
                  disabled={!state.validation?.pageValid}
                  baseColor
                />
              </Row>
            )}

            {state.confirmPage && (
              <Button
                onClick={(endWizard)}
                label={state.confirmLabel ?? translate('confirm')}
                imgSrc="/images/svg/gui/check.svg"
                pending={state.pageBusy || state.waitingOnPageInfo}
                disabled={!state.validation?.pageValid}
                baseColor
              />
            )}
          </Row>
        </>
      );
    },
    [
      configReady, state, pages, translate,
      backClick, nextClick, endWizard,
    ],
  );

  // Get the title for the current page
  const currentPageTitle = useMemo(
    () => {
      const currentPageInfo = pages[state.currentPage];

      return currentPageInfo?.title || '';
    },
    [pages, state.currentPage],
  );

  // The steps bar reads the pages in their declaration order; wizards whose
  // path branches at runtime should not show it
  const steps = useMemo(
    () => Object.keys(pages).map((key) => ({
      key,
      lbl: pages[key].stepLbl,
    })),
    [pages],
  );

  return (
    <WizardContext.Provider value={wizardContextValue}>
      <FullPopup
        onClose={cancel}
        title={currentPageTitle}
        mobileFullScreen={mobileFullScreen}
        navigationBar={(showProgressBar)
          ? <WizardStepsBar steps={steps} currentKey={state.currentPage} narrow={narrow} />
          : undefined}
        contentClassName="flex flex-col items-start justify-start overflow-hidden gap-4 flex-1 min-h-0"
      >
        {content}

        {state.confirmClosePending && (
          <ConfirmPopup
            confirmCritical
            question={translate('confirmCancelEdit')}
            onConfirm={resetAndClose}
            onReject={rejectCancel}
          />
        )}

        {state.confirmActionPending && state.confirmOptions && (
          <ConfirmPopup
            question={translate(state.confirmOptions.confirmMessage, 'questions')}
            onConfirm={confirmAction}
            onReject={rejectAction}
            confirmText={state.confirmOptions.confirmText}
          />
        )}
      </FullPopup>
    </WizardContext.Provider>
  );
}

WizardPlayer.propTypes = {
  pages: PropTypes.objectOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    // Named on the steps bar when it is the current one
    stepLbl: PropTypes.string,
    Content: PropTypes.elementType.isRequired,
  })).isRequired,
  closeCreate: PropTypes.func.isRequired,
  initialPage: PropTypes.string,
  defaultPageState: PropTypes.shape({}),
  showProgressBar: PropTypes.bool,
  // Set on the public routes, which carry no customer configuration to wait for
  noTownConfig: PropTypes.bool,
  // Fill the screen edge to edge on a phone-sized window
  mobileFullScreen: PropTypes.bool,
};

export { WizardPlayer };
